import React, { useRef, useState, useEffect } from "react";
import { Trash2, CheckCircle, XCircle, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import useApi from "../../../hook/req";
import * as XLSX from 'xlsx';
import { SingleAudienceResponse } from "../../../../pages/campaign/types";

interface UploadTabProps {
  channel: string;
  onUploadSuccess: (audienceId: string, fileName: string, contacts: number) => void;
  audienceById: SingleAudienceResponse | null;
  onValidation: (isValid: boolean) => void;
}

interface UploadResults {
  fileName: string;
  totalRecords: number;
  validRecords: number;
  invalidRecords: number;
  duplicateRecords: number;
  preview?: Array<{
    sno: number;
    contact: string;
    status: string;
  }>;
}

const MAX_FILE_SIZE_MB = 50;
const ACCEPTED_EXTENSIONS = [".csv", ".xlsx", ".xls"];

const UploadTab: React.FC<UploadTabProps> = ({
  channel,
  onUploadSuccess,
  audienceById,
  onValidation,
}) => {
  const token = localStorage.getItem("authToken");

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResults | null>(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [uploadedAudienceId, setUploadedAudienceId] = useState<string | null>(null);

  const uploadInProgress = useRef(false);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  const { makeRequest: uploadFileAudience, loading: fileUploadLoading } = useApi(
    "/audience/upload/csv",
    "POST",
    { Authorization: `Bearer ${token}` }
  );

  const isEmailCampaign = channel === "email";

  // ====================== PREFILL EXISTING AUDIENCE ======================
  useEffect(() => {
    if (!audienceById || audienceById.type === "manual") return;

    const totalRecords = audienceById.total_records ?? 0;
    if (totalRecords === 0) return;

    let fileName = "audience_file";
    if (audienceById.file_url) {
      const urlParts = audienceById.file_url.split("/");
      fileName = urlParts[urlParts.length - 1] || fileName;
      fileName = fileName.replace(/_\w{8}-\w{4}-\w{4}-\w{4}-\w{12}/, "");
    } else if (audienceById.audience_id) {
      fileName = `audience_${audienceById.audience_id.slice(0, 8)}.csv`;
    }

    const results: UploadResults = {
      fileName,
      totalRecords,
      validRecords: audienceById.valid_records ?? 0,
      invalidRecords: audienceById.invalid_records ?? 0,
      duplicateRecords: audienceById.duplicate_records ?? 0,
      preview: [{ sno: 1, contact: "Loaded from previously uploaded file", status: "valid" }],
    };

    setUploadResults(results);
    setUploadedAudienceId(audienceById.audience_id);
    setHasUploaded(true);
    setSelectedFile(null);

    onUploadSuccess(audienceById.audience_id, fileName, audienceById.valid_records ?? 0);
    onValidation((audienceById.valid_records ?? 0) > 0);
  }, [audienceById]);

  // ====================== VALIDATION & PARSING ======================
  const isValidPhone = (phone: string): boolean => {
    if (!phone) return false;
    let clean = phone.replace(/\D/g, "");
    if (clean.startsWith("234")) clean = "0" + clean.slice(3);
    return (clean.length === 11 && clean.startsWith("0")) || clean.length === 10;
  };

  const normalizePhone = (phone: string): string | null => {
    let clean = phone.replace(/\D/g, "");
    if (clean.startsWith("234")) clean = "0" + clean.slice(3);
    if (clean.length === 10) clean = "0" + clean;
    return clean.length === 11 && clean.startsWith("0") ? clean : null;
  };

  const isValidEmail = (email: string): boolean => {
    if (!email?.trim()) return false;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email.trim());
  };

  const validateContact = (contact: string): boolean =>
    isEmailCampaign ? isValidEmail(contact) : isValidPhone(contact);

  const parseCSV = (text: string): string[] => {
    const lines = text.split("\n");
    const firstLine = lines[0]?.trim().toLowerCase() || "";
    const isHeader = /phone|email|contact|number/.test(firstLine);
    const startIndex = isHeader ? 1 : 0;
    const contacts: string[] = [];
    for (let i = startIndex; i < lines.length; i++) {
      const firstCol = lines[i].split(",")[0]?.trim();
      if (firstCol) contacts.push(firstCol);
    }
    return contacts;
  };

  const parseXLSX = (file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
          const firstRow = rows[0] || [];
          const isHeader = firstRow.some((cell) =>
            typeof cell === "string" && /phone|email|contact|number/i.test(cell)
          );
          const startIndex = isHeader ? 1 : 0;
          const contacts: string[] = [];
          for (let i = startIndex; i < rows.length; i++) {
            const cell = rows[i]?.[0];
            if (cell) contacts.push(String(cell).trim());
          }
          resolve(contacts);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  };

  const processFile = async (file: File) => {
    setIsParsing(true);
    setUploadError(null);

    try {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      let contacts: string[] = [];

      if (ext === ".csv") contacts = parseCSV(await file.text());
      else if (ext === ".xlsx" || ext === ".xls") contacts = await parseXLSX(file);
      else throw new Error("Unsupported file format");

      if (contacts.length === 0) throw new Error("No contacts found in file");

      const seen = new Set<string>();
      let validCount = 0, invalidCount = 0, duplicateCount = 0;
      const preview: UploadResults["preview"] = [];

      contacts.forEach((contact, index) => {
        let status: string = "invalid";
        if (validateContact(contact)) {
          const normalized = isEmailCampaign ? contact.toLowerCase() : normalizePhone(contact);
          if (normalized) {
            if (seen.has(normalized)) {
              status = "duplicate";
              duplicateCount++;
            } else {
              seen.add(normalized);
              status = "valid";
              validCount++;
            }
          }
        } else {
          invalidCount++;
        }
        if (index < 5) preview.push({ sno: index + 1, contact, status });
      });

      const results: UploadResults = {
        fileName: file.name,
        totalRecords: contacts.length,
        validRecords: validCount,
        invalidRecords: invalidCount,
        duplicateRecords: duplicateCount,
        preview,
      };

      setUploadResults(results);
      setSelectedFile(file);
      setHasUploaded(false);
      setUploadedAudienceId(null);
      onValidation(validCount > 0);

      // Auto upload after parsing
      setTimeout(() => uploadFileImmediately(file), 400);
    } catch (error: any) {
      setUploadError(error.message || "Failed to parse file");
      console.error(error);
    } finally {
      setIsParsing(false);
    }
  };

  const validateFile = (file: File): boolean => {
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setUploadError("Only CSV or XLSX files are accepted.");
      return false;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setUploadError(`File must be under ${MAX_FILE_SIZE_MB}MB.`);
      return false;
    }
    if (file.size === 0) {
      setUploadError("File is empty.");
      return false;
    }
    return true;
  };

  const resetUploadState = () => {
    setSelectedFile(null);
    setUploadResults(null);
    setUploadError(null);
    setUploadProgress(0);
    setIsProcessing(false);
    setIsParsing(false);
    setHasUploaded(false);
    setUploadedAudienceId(null);
    uploadInProgress.current = false;
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = "";
  };

  const handleFileSelect = (file: File) => {
    resetUploadState();
    if (!validateFile(file)) return;
    setSelectedFile(file);
    processFile(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
  };

  const handleRemoveFile = () => resetUploadState();

  // ====================== UPLOAD TO SERVER (FIXED MAPPING) ======================
  const uploadFileImmediately = async (file: File) => {
    if (uploadInProgress.current || !file) return;

    uploadInProgress.current = true;
    setIsProcessing(true);
    setUploadProgress(0);
    setUploadError(null);

    const progressInterval = setInterval(() => {
      setUploadProgress((p) => (p >= 90 ? 90 : p + 10));
    }, 120);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const [response] = await uploadFileAudience(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response?.metadata) {
        const meta = response.metadata;

        const total = meta.total_count ?? 0;
        const valid = meta.unique_count ?? 0;
        const duplicates = meta.duplicates_count ?? 0;
        const invalids = meta.invalids_count ?? 0;

        setUploadResults((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            totalRecords: total,
            validRecords: valid,
            invalidRecords: invalids,
            duplicateRecords: duplicates,
          };
        });

        const audienceId = response.audience_id || response.id;
        setUploadedAudienceId(audienceId);
        setHasUploaded(true);

        toast.success(`Upload successful! ${valid} valid contacts`);
        onUploadSuccess(audienceId, file.name, valid);
        onValidation(valid > 0);
      } else {
        throw new Error("Invalid server response");
      }
    } catch (error) {
      console.error(error);
      toast.error("Upload failed");
      setUploadError("Failed to upload file to server");
      onValidation(false);
    } finally {
      setIsProcessing(false);
      uploadInProgress.current = false;
    }
  };

  const isExistingAudience = !!audienceById &&
    audienceById.type !== "manual" &&
    (audienceById.total_records ?? 0) > 0;

  const showExistingOnly = isExistingAudience && !selectedFile;

  return (
    <div className="space-y-6">
  

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-2xl px-6 py-12 bg-[#F3F8F6] text-center transition-all ${
          dragActive ? "border-teal-500 bg-teal-50" : "border-gray-300"
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <p className="text-teal-600 font-medium mb-4">
          {showExistingOnly ? "Upload a new file or continue with existing" : "Drag & drop your contact list"}
        </p>
        <button
          onClick={() => uploadFileInputRef.current?.click()}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-medium transition"
          type="button"
        >
          {showExistingOnly ? "Upload New File" : "Browse Files"}
        </button>
        <p className="text-gray-500 text-sm mt-4">
          CSV or XLSX • Max {MAX_FILE_SIZE_MB}MB
        </p>
        <input
          ref={uploadFileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Progress */}
      {(isParsing || isProcessing) && (
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-center mb-3">
            <FileText className="w-5 h-5 text-blue-600 mr-2" />
            <p className="font-medium">
              {isParsing ? "Parsing file..." : "Uploading to server..."}
            </p>
          </div>
          <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: isParsing ? "60%" : `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* File Info */}
      {(selectedFile || showExistingOnly) && uploadResults && (
        <div className="bg-white border rounded-xl p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-[#4338CA]">{uploadResults.fileName}</p>
              <div className="flex gap-4 text-xs mt-1 text-gray-600">
                <span>Total: {uploadResults.totalRecords}</span>
                <span className="text-green-600">Valid: {uploadResults.validRecords}</span>
                {uploadResults.invalidRecords > 0 && (
                  <span className="text-red-600">Invalid: {uploadResults.invalidRecords}</span>
                )}
                {uploadResults.duplicateRecords > 0 && (
                  <span className="text-yellow-600">Duplicates: {uploadResults.duplicateRecords}</span>
                )}
              </div>
            </div>
          </div>
          {selectedFile && (
            <button onClick={handleRemoveFile} className="text-gray-400 hover:text-red-500 transition">
              <Trash2 size={18} />
            </button>
          )}
        </div>
      )}

      {/* Error */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <XCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{uploadError}</p>
        </div>
      )}

      {/* Manual Upload Button (fallback) */}
      {uploadResults && !hasUploaded && !isProcessing && !isParsing && selectedFile && (
        <div className="flex justify-center pt-4">
          <button
            onClick={() => uploadFileImmediately(selectedFile)}
            disabled={uploadResults.validRecords === 0}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 text-white px-10 py-3.5 rounded-xl font-medium flex items-center gap-2 transition"
          >
            <CheckCircle className="w-5 h-5" />
            Upload Now ({uploadResults.validRecords} contacts)
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadTab;